import type { FriendsStore } from "../online/friends.js";
import { t } from "../i18n/index.js";
import { escapeHtml } from "./careerText.js";
import { rivalryLine } from "./friendText.js";

/**
 * "Con quien has jugado", en la pantalla de resultados de un partido online.
 *
 * La forma natural de hacer amigos en un juego es jugar con alguien y querer
 * repetir, y ese momento es ESTE: acabas de meterle un gol o de ganar con el.
 * Antes estaban en una lista de "Jugaste con" dentro de Amigos, en el lobby,
 * donde ya no se acordaba nadie.
 *
 * Con los que ya son amigos, el pique: "A Mike le llevas 5-3". El numero sale
 * del arbitro del servidor, y llega un momento despues de acabar (rehace el
 * partido); por eso se repinta cuando cambia la lista de amigos.
 */
export interface Mate {
  clientId: string;
  nickname: string;
  /** De tu equipo en este partido. */
  teammate: boolean;
}

export function mountMatesStrip(slot: HTMLElement, mates: Mate[], friends: FriendsStore): () => void {
  if (mates.length === 0) return () => {};
  const asked = new Set<string>();

  const render = (): void => {
    const state = friends.state();
    const rows = mates
      .map((m) => {
        const friend = state?.friends.find((f) => f.clientId === m.clientId);
        const incoming = state?.incoming.some((r) => r.clientId === m.clientId) ?? false;
        const outgoing = asked.has(m.clientId) || (state?.outgoing.some((r) => r.clientId === m.clientId) ?? false);
        const blocked = state?.blocked.some((r) => r.clientId === m.clientId) ?? false;
        if (blocked) return "";
        const role = m.teammate ? t("compañero") : t("rival");
        let right: string;
        let line = "";
        if (friend) {
          right = `<span class="mates-tag is-friend">${escapeHtml(t("Amigo"))}</span>`;
          line = rivalryLine(m.nickname, friend.vs) ?? "";
        } else if (incoming) {
          right = `<button type="button" class="mates-btn is-yes" data-accept="${escapeHtml(m.clientId)}">${escapeHtml(t("Aceptar"))}</button>`;
        } else if (outgoing) {
          right = `<span class="mates-tag">${escapeHtml(t("Solicitud enviada"))}</span>`;
        } else {
          right = `<button type="button" class="mates-btn is-yes" data-add="${escapeHtml(m.clientId)}">${escapeHtml(t("+ Añadir"))}</button>`;
        }
        return `
          <li class="mates-row">
            <span class="mates-who">
              <strong>${escapeHtml(m.nickname)}</strong>
              <small>${escapeHtml(role)}${line ? ` · ${escapeHtml(line)}` : ""}</small>
            </span>
            ${right}
          </li>`;
      })
      .join("");
    slot.innerHTML = rows
      ? `<section class="rs-block mates-block">
          <div class="rs-block-head"><span>${escapeHtml(t("Has jugado con"))}</span></div>
          <ul class="mates-list">${rows}</ul>
        </section>`
      : "";
  };

  const onClick = (ev: Event): void => {
    const el = (ev.target as HTMLElement).closest<HTMLElement>("[data-add],[data-accept]");
    if (!el) return;
    ev.stopPropagation();
    if (el.dataset.add) {
      asked.add(el.dataset.add);
      friends.addById(el.dataset.add);
    } else if (el.dataset.accept) {
      friends.respond(el.dataset.accept, true);
    }
    render();
  };
  slot.addEventListener("click", onClick);
  const unsub = friends.subscribe(render);
  return () => {
    unsub();
    slot.removeEventListener("click", onClick);
  };
}
